import { useEffect, useRef, useState } from 'react';

type Options = {
  /** Automatically open after delay (default: true) */
  autoOpen?: boolean;
  /** Restart timer when trigger changes (default: true) */
  restartOnTriggerChange?: boolean;
};

/**
 * Reusable hook to automatically open something (modal, popover, etc.)
 * after a delay or when a trigger changes.
 */
export function useAutoOpenAfterDelay<T = unknown>(
  delayMs: number = 0,
  trigger?: T,
  { autoOpen = true, restartOnTriggerChange = true }: Options = {}
) {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<null | number>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!autoOpen) return;
    clearTimer();

    timerRef.current = window.setTimeout(
      () => {
        setIsOpen(true);
        timerRef.current = null;
      },
      Math.max(0, delayMs)
    );

    return clearTimer;
  }, [autoOpen, delayMs, ...(restartOnTriggerChange ? [trigger] : [])]);

  useEffect(() => clearTimer, []);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  return { close, isOpen, open, setIsOpen };
}
